import _ from 'underscore';

import { Filter } from '~commonInfra/utils/Filter';
import { ServerApiMapper } from '~commonInfra/utils/ServerModelHelper';
import { GeneratePublicKeyRequest, IGeneratePublicKeyResult } from '~src/authentication/serverApi/requests/GeneratePublicKeyRequest';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { SiteRoleFilterHelper } from '~src/siteManagement/helpers/SiteRoleFilterHelper';
import { ISelectFilterDefinitionOption } from '~src/spacesPlatform/services/FilterPanelService';
import { FilterMap } from '~src/spacesPlatform/state/ContentExplorerState';
import { GetServerUserMaxSiteRoleCountsRequest,IGetServerUserMaxSiteRoleCountsParams, IGetServerUserMaxSiteRoleCountsResult } from '~src/userManagement/users/serverApi/requests/GetServerUserMaxSiteRoleCountsRequest';

import { ForcePasswordUpdateRequest } from '../../../authentication/serverApi/requests/ForcePasswordUpdateRequest';
import { CheckPersonalAccessTokenCreationParams, CheckPersonalAccessTokenCreationRequest } from '../../../authentication/serverApi/requests/personalAccess/CheckPersonalAccessTokenCreationRequest';
import { GetPersonalAccessTokenClientIdsParams, GetPersonalAccessTokenClientIdsRequest, GetPersonalAccessTokenClientIdsResult } from '../../../authentication/serverApi/requests/personalAccess/GetPersonalAccessTokenClientIdsRequest';
import { RevokePersonalAccessTokenByClientIdParams, RevokePersonalAccessTokenByClientIdRequest } from '../../../authentication/serverApi/requests/personalAccess/RevokePersonalAccessTokenByClientIdRequest';
import { ResetLoginRateLimitingOnSiteUsersRequest } from '../../../authentication/serverApi/requests/ResetLoginRateLimitingOnSiteUsersRequest';
import { ResetLoginRateLimitingRequest } from '../../../authentication/serverApi/requests/ResetLoginRateLimitingRequest';
import { IPersonalAccessToken } from '../../../authentication/serverApi/types/personalAccess/IPersonalAccessToken';
import { ListResult } from '../../../httpClient/fetchers/ListResult';
import { WebClientListResourceFetcher } from '../../../httpClient/fetchers/resourceFetchers/WebClientListResourceFetcher';
import { Resource } from '../../../httpClient/resources/Resource';
import { IResourceQuery } from '../../../httpClient/resources/ResourceQuery';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import { RequestHelper } from '../../../httpClient/utils/RequestHelper';
import { ResultHelper } from '../../../httpClient/utils/ResultHelper';
import { WebHttpClientWithErrorToastInstance } from '../../../httpClient/WebHttpClientWithErrorToast';
import { FilterFieldNames, ICompositeFilterClause, IFieldFilterClause, IStringValuedFilterClause } from '../../../spacesPlatform/models/IFilterClause';
import { NamedItemKeys } from '../../../spacesPlatform/models/resourceParts';
import { GetUserSettingsRequest, IGetUserSettingsParams, IGetUserSettingsResult } from '../../../userSettings/serverApi/requests/GetUserSettingsRequest';
import { IServerUserInfo } from '../../users/models/IServerUserInfo';
import { CreateLocalServerUserRequest } from '../../users/serverApi/requests/CreateLocalServerUserRequest';
import { CreateLocalServerUserWithoutPasswordRequest } from '../../users/serverApi/requests/CreateLocalServerUserWithoutPasswordRequest';
import { DeleteServerUsersRequest } from '../../users/serverApi/requests/DeleteServerUsersRequest';
import { DeleteUserConnectedDevicesRequest, IDeleteUserConnectedDevicesParams } from '../../users/serverApi/requests/DeleteUserConnectedDevicesRequest';
import { DeleteUserRemoteAgentsRequest, IDeleteUserRemoteAgentsParams } from '../../users/serverApi/requests/DeleteUserRemoteAgentsRequest';
import { ForcePasswordUpdateOnSiteUsersRequest } from '../../users/serverApi/requests/ForcePasswordUpdateOnSiteUsers';
import { GetServerUsersRequest } from '../../users/serverApi/requests/GetServerUsersRequest';
import { GetUserConnectedDevicesRequest, IGetUserConnectedDevicesParams, IGetUserConnectedDevicesResult } from '../../users/serverApi/requests/GetUserConnectedDevicesRequest';
import { GetUserRefreshTokenCountRequest, IGetUserRefreshTokenCountParams, IGetUserRefreshTokenCountResult } from '../../users/serverApi/requests/GetUserRefreshTokenCountRequest';
import { GetUsersSiteRolesRequest, IGetUsersSiteRolesResult } from '../../users/serverApi/requests/GetUsersSiteRolesRequest';
import { ImportServerUsersFromActiveDirectoryRequest } from '../../users/serverApi/requests/ImportUsersFromActiveDirectoryRequest';
import { UpdateServerUsersAdminStatusRequest } from '../../users/serverApi/requests/UpdateServerUsersAdminStatusRequest';
import { IUpdateServerUsersSiteMembershipResult, UpdateServerUsersSiteMembershipRequest } from '../../users/serverApi/requests/UpdateServerUsersSiteMembershipRequest';
import { IConnectedDevice } from '../../users/serverApi/types/connectedDevices/IConnectedDevice';
import { ISiteRole } from '../../users/serverApi/types/ISiteRole';
import { IUserActionResult } from '../../users/serverApi/types/IUserActionResult';
import { SiteRolesResource } from './SiteRoles';

export class ServerUsers extends Resource<IServerUserInfo> {

  public static readonly instance = new ServerUsers(new WebClientListResourceFetcher(GetServerUsersRequest, ServerApiMapper.serverUsers));

  // returns the server user with the specified username & domainName.
  public getUserByUsernameAndDomain(username: string, domainName: string): Promise<IServerUserInfo> {
    return this.all()
      .addFilter(Filter.stringFieldEq('username', username))
      .addFilter(Filter.stringFieldEq('domainName', domainName))
      .getFirst();
  }

  // returns the server users with the specified IDs
  public getByUserIds(userIds: string[]): Promise<ListResult<IServerUserInfo>> {
    let query = this.all();
    const usersFilter = Filter.stringFieldInArray(NamedItemKeys.id, userIds);
    query = query.addFilter(usersFilter);

    return query.getCount().then((totalCount: number) => {
      return query.getSlice(0, totalCount);
    });
  }

  public getUserCountsByMaxSiteRole(baseQuery: IResourceQuery<IServerUserInfo>): Promise<_.Dictionary<number>> {

    // Remove any existing max site role filter
    const filtersWithoutMaxSiteRole = _.filter(baseQuery.getFilters(), (filter: IFieldFilterClause) => {
      return !(filter.field && filter.field === FilterFieldNames.maxSiteRole);
    });
    const modifiedQuery = baseQuery.withFilters(filtersWithoutMaxSiteRole);

    const counts: _.Dictionary<number> = {};

    const promises = _.map(SiteRolesResource.instance.optionsForCurrentUser(), (maxSiteRole: string) => {
      return modifiedQuery
        .addFilter(Filter.stringFieldEq(FilterFieldNames.maxSiteRole, maxSiteRole))
        .getCount()
        .then((count: number) => {
          counts[maxSiteRole] = count;
        });
    });

    // Any site role
    promises.push(modifiedQuery.getCount().then((count: number) => { counts['Any'] = count; }));

    return Promise.all(promises).then(() => counts);
  }

  public async getUserCountsByMaxSiteRoleWithoutSolr(mixedContentFilterMap: FilterMap): Promise<ISelectFilterDefinitionOption<string>[]> {

    let filterClauses = [];
    if (this.searchTerm(mixedContentFilterMap)) {
      filterClauses.push(mixedContentFilterMap.search);
    }
    if (this.identityPoolFilterTerm(mixedContentFilterMap)) {
      filterClauses.push(mixedContentFilterMap.identityPool);
    }
    const filter = (filterClauses && filterClauses.length > 0) ? Filter.and(filterClauses) : undefined;
    const params: IGetServerUserMaxSiteRoleCountsParams = {
      filter: filter
    };
    const maxSiteRoleCountsForServerUsers: IGetServerUserMaxSiteRoleCountsResult  = await WebHttpClientWithErrorToastInstance.sendRequest(new GetServerUserMaxSiteRoleCountsRequest(params));

    const counts: _.Dictionary<number> = {};

    for (const siteRoleCount of maxSiteRoleCountsForServerUsers.siteRoleCounts) {
      counts[siteRoleCount.label] =  siteRoleCount.count;
    }
    counts['Any'] = maxSiteRoleCountsForServerUsers.totalUserCount;
    return SiteRoleFilterHelper.siteRoleOptions(SiteRolesResource.instance.optionsForCurrentUser(), counts);
  }

  private searchTerm = (mixedContentFilterMap: FilterMap) => {
    return (mixedContentFilterMap.search
      && (mixedContentFilterMap.search as IStringValuedFilterClause).value)
      || '';
  };

  private identityPoolFilterTerm = (mixedContentFilterMap: FilterMap) => {
    return (mixedContentFilterMap.identityPool
      && (mixedContentFilterMap.identityPool as IStringValuedFilterClause).value)
      || '';
  };

  public async getUserTotalCountWithoutSolr(filter: ICompositeFilterClause): Promise<number> {
    const params: IGetServerUserMaxSiteRoleCountsParams = {
      filter: filter
    };
    const maxSiteRoleCountsForServerUsers: IGetServerUserMaxSiteRoleCountsResult  = await WebHttpClientWithErrorToastInstance.sendRequest(new GetServerUserMaxSiteRoleCountsRequest(params));

    return maxSiteRoleCountsForServerUsers.totalUserCount;
  }

  public getUserSettings(username: string, domain: string): Promise<IGetUserSettingsResult> {
    const params: IGetUserSettingsParams = {
      username: username,
      domainName: domain
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new GetUserSettingsRequest(params));
  }

  public getUserRefreshTokenCount(username: string, domain: string): Promise<number> {
    const params: IGetUserRefreshTokenCountParams = {
      username: username,
      domainName: domain
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new GetUserRefreshTokenCountRequest(params))
      .then((result: IGetUserRefreshTokenCountResult) => result.refreshTokenCount);
  }

  public getUserConnectedDevices(userId: string): Promise<IConnectedDevice[]> {
    const params: IGetUserConnectedDevicesParams = {
      userId: userId
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new GetUserConnectedDevicesRequest(params))
      .then((result: IGetUserConnectedDevicesResult) => result.devices);
  }

  public deleteUserConnectedDevices(userId: string, deviceIds: string[]): Promise<IResult> {
    const params: IDeleteUserConnectedDevicesParams = {
      userId: userId,
      deviceIds: deviceIds
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new DeleteUserConnectedDevicesRequest(params));
  }

  public checkPersonalAccessTokenCreationIsAllowed(): Promise<IResult> {
    const params: CheckPersonalAccessTokenCreationParams = {
    };
    return WebHttpClient.sendRequest(new CheckPersonalAccessTokenCreationRequest(params));
  }

  public getPersonalAccessTokens(userId: string): Promise<IPersonalAccessToken[]> {
    const params: GetPersonalAccessTokenClientIdsParams = {
      userId: userId
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new GetPersonalAccessTokenClientIdsRequest(params))
      .then((result: GetPersonalAccessTokenClientIdsResult) => result.tokenInfos);
  }

  public deletePersonalAccessToken(userId: string, tokenName: string): Promise<IResult> {
    const params: RevokePersonalAccessTokenByClientIdParams = {
      userId: userId,
      tokenName: tokenName
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new RevokePersonalAccessTokenByClientIdRequest(params));
  }

  public deleteUserRemoteAgents(ownerId: string, deviceIds: string[]): Promise<IResult> {
    const params: IDeleteUserRemoteAgentsParams = {
      ownerId: ownerId,
      deviceIds: deviceIds
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new DeleteUserRemoteAgentsRequest(params));
  }

  // returns the server users' roles across all sites
  public getSiteRoles(userIds: string[]): Promise<IGetUsersSiteRolesResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new GetUsersSiteRolesRequest({ ids: userIdsBlock })),
      (results: IGetUsersSiteRolesResult[]) => {
        return {
          errors: ResultHelper.combineErrors(results),
          users: ResultHelper.combine(results, (result) => result.users)
        };
      });
  }

  // site membership - add/remove server users from sites
  public updateServerUsersSiteMembership(userIds: string[], addToSiteRoles: ISiteRole[], removeFromSiteIds: string[]): Promise<IUpdateServerUsersSiteMembershipResult>
  {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests3(userIds, addToSiteRoles, removeFromSiteIds,
        (userIdsBlock, addToSiteRolesBlock, removeFromSiteIdsBlock) => new UpdateServerUsersSiteMembershipRequest({
          userIds: userIdsBlock,
          addToSiteRoles: addToSiteRolesBlock,
          removeFromSiteIds: removeFromSiteIdsBlock
        })),
        (results: IUpdateServerUsersSiteMembershipResult[]) => {
          let res = ResultHelper.combine(results, (result) => result.results);
          let err = ResultHelper.combineErrors(results);
          return {
            results: res,
            errors: err ? err : []
          };
      });
  }

  // site membership - change these users' server admin status
  public updateServerAdminStatus(userIds: string[], admin: boolean): Promise<IResult>
  {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new UpdateServerUsersAdminStatusRequest({
        userIds: userIdsBlock,
        admin: admin,
      })),
      ResultHelper.combineResults);
  }

  // force a password update on the users provided
  public forcePasswordUpdate(userIds: string[]): Promise<IUserActionResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new ForcePasswordUpdateRequest({ systemUserIds: userIdsBlock })),
      ResultHelper.combineUserResults);
  }

  // force a password update on the site users provided
  public forcePasswordUpdateOnSiteUsers(userIds: string[]): Promise<IUserActionResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new ForcePasswordUpdateOnSiteUsersRequest({ siteUserIds: userIdsBlock })),
      ResultHelper.combineUserResults);
  }

  // force a password update on the users provided
  public resetLoginRateLimiting(userIds: string[]): Promise<IUserActionResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new ResetLoginRateLimitingRequest({ systemUserIds: userIdsBlock })),
      ResultHelper.combineUserResults);
  }

  // force a password update on the site users provided
  public resetLoginRateLimitingOnSiteUsers(userIds: string[]): Promise<IUserActionResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new ResetLoginRateLimitingOnSiteUsersRequest({ siteUserIds: userIdsBlock })),
      ResultHelper.combineUserResults);
  }

  // delete users from server
  public deleteUsers(userIds: string[]): Promise<IUserActionResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new DeleteServerUsersRequest({ ids: userIdsBlock })),
      ResultHelper.combineUserResults);
  }

  // import users from active directory to server
  public importUsersFromActiveDirectory(usernames: string[], identityPoolName: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(usernames,
        (usernamesBlock) => new ImportServerUsersFromActiveDirectoryRequest({usernames: usernamesBlock, identityPoolName: identityPoolName})),
      ResultHelper.combineImportUserResults);
  }

  // create a new local server user
  public createLocalServerUser(
    username: string,
    displayName: string,
    email: string,
    password: string,
    identityPoolName?: string,
    identifier?: string): Promise<IResult> {

    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new GeneratePublicKeyRequest()).
      then((generatePublicKeyResult: IGeneratePublicKeyResult) => {
        const key = generatePublicKeyResult.key;
        const keyId = generatePublicKeyResult.keyId;
        const encryptedPassword = rsa.encrypt(password, key);
        const createLocalServerUserRequest = new CreateLocalServerUserRequest({
          username: username,
          displayName: displayName,
          email: email,
          encryptedPassword: encryptedPassword,
          keyId: keyId,
          identityPoolName: identityPoolName,
          identifier: identifier
        });
        return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(createLocalServerUserRequest);
      });
  }

    // create a new local server user when SAML or SiteSAML is enabled
  public createLocalServerUserWithoutPassword(
    username: string,
    displayName: string,
    email: string,
    identityPoolName?: string,
    identifier?: string): Promise<IResult> {
        const createLocalServerRequest = new CreateLocalServerUserWithoutPasswordRequest({
          username: username,
          displayName: displayName,
          email: email,
          identityPoolName: identityPoolName,
          identifier: identifier
        });
        return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(createLocalServerRequest);
  }
}

export default ServerUsers.instance;
